import React, { useState, useEffect } from 'react';
import './BgCalculator.css';

const BgCalculator = () => {
    const [activeCalculator, setActiveCalculator] = useState('gst');
    const [isMobile, setIsMobile] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    // GST Calculator State
    const [gstData, setGstData] = useState({
        amount: '',
        rate: '', // BLANK by default
        customRate: ''
    });
    const [gstResult, setGstResult] = useState(null);
    const [gstErrors, setGstErrors] = useState({});

    // Basic Calculator State
    const [basicData, setBasicData] = useState({
        expression: '',
        num1: '',
        num2: '',
        operation: '+'
    });
    const [basicResult, setBasicResult] = useState(null);
    const [basicErrors, setBasicErrors] = useState({});

    // Tip Calculator State
    const [tipData, setTipData] = useState({
        billAmount: '',
        tipPercentage: '15',
        customTip: '',
        splitCount: '1'
    });
    const [tipResult, setTipResult] = useState(null);
    const [tipErrors, setTipErrors] = useState({});

    // Currency Converter State
    const [currencyData, setCurrencyData] = useState({
        amount: '',
        fromCurrency: 'INR',
        toCurrency: 'USD'
    });
    const [currencyResult, setCurrencyResult] = useState(null);
    const [currencyErrors, setCurrencyErrors] = useState({});

    // Unit Converter State
    const [unitData, setUnitData] = useState({
        value: '',
        fromUnit: 'meter',
        toUnit: 'kilometer',
        category: 'length'
    });
    const [unitResult, setUnitResult] = useState(null);
    const [unitErrors, setUnitErrors] = useState({});

    // Discount Calculator State
    const [discountData, setDiscountData] = useState({
        originalPrice: '',
        discountPercent: '',
        discountAmount: ''
    });
    const [discountResult, setDiscountResult] = useState(null);
    const [discountErrors, setDiscountErrors] = useState({});

    // Loan Calculator State
    const [loanData, setLoanData] = useState({
        principal: '',
        interestRate: '',
        loanTerm: '',
        termType: 'years'
    });
    const [loanResult, setLoanResult] = useState(null);
    const [loanErrors, setLoanErrors] = useState({});

    // BMI Calculator State
    const [bmiData, setBmiData] = useState({
        height: '',
        weight: '',
        heightUnit: 'cm',
        weightUnit: 'kg'
    });
    const [bmiResult, setBmiResult] = useState(null);
    const [bmiErrors, setBmiErrors] = useState({});

    // Calculator types
    const calculatorTypes = [
        { id: 'gst', name: 'GST Calculator', icon: 'receipt' },
        { id: 'basic', name: 'Basic Calculator', icon: 'calculator' },
        { id: 'tip', name: 'Tip Calculator', icon: 'utensils' },
        { id: 'currency', name: 'Currency Converter', icon: 'money-bill-wave' },
        { id: 'unit', name: 'Unit Converter', icon: 'ruler' },
        { id: 'discount', name: 'Discount Calculator', icon: 'tag' },
        { id: 'loan', name: 'Loan Calculator', icon: 'home' },
        { id: 'bmi', name: 'BMI Calculator', icon: 'weight' }
    ];

    // Check for mobile screen
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Handle calculator type selection
    const handleCalculatorSelect = (calcId) => {
        setActiveCalculator(calcId);
        if (isMobile) {
            setShowDropdown(false);
        }
    };

    // Common validation functions
    const validatePositiveNumber = (value, fieldName) => {
        if (value === '' || value === null || value === undefined) {
            return `${fieldName} is required`;
        }

        const num = parseFloat(value);
        if (isNaN(num)) {
            return `${fieldName} must be a valid number`;
        }

        if (num < 0) {
            return `${fieldName} cannot be negative`;
        }

        if (num === 0) {
            return `${fieldName} must be greater than zero`;
        }

        return '';
    };

    const validatePercentage = (value, fieldName) => {
        if (value === '' || value === null || value === undefined) {
            return `${fieldName} is required`;
        }

        const num = parseFloat(value);
        if (isNaN(num)) {
            return `${fieldName} must be a valid number`;
        }

        if (num < 0) {
            return `${fieldName} cannot be negative`;
        }

        if (num > 100) {
            return `${fieldName} cannot exceed 100%`;
        }

        return '';
    };

    // Handle GST calculation
    const handleGstCalculate = () => {
        const errors = {};

        // Validate amount
        const amountError = validatePositiveNumber(gstData.amount, 'Amount');
        if (amountError) errors.amount = amountError;

        // Validate rate - GST rate is required
        let rate = gstData.rate;
        if (rate === '') {
            errors.rate = 'Please select GST rate';
        } else if (rate === 'custom') {
            const rateError = validatePercentage(gstData.customRate, 'GST Rate');
            if (rateError) errors.customRate = rateError;
            rate = gstData.customRate;
        } else {
            const rateError = validatePercentage(rate, 'GST Rate');
            if (rateError) errors.rate = rateError;
        }

        setGstErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        const amount = parseFloat(gstData.amount);
        const gstRate = parseFloat(rate);

        const gstAmount = (amount * gstRate) / 100;
        const totalWithGst = amount + gstAmount;
        const cgst = gstAmount / 2;
        const sgst = gstAmount / 2;

        setGstResult({
            originalAmount: amount,
            gstRate: gstRate,
            gstAmount: gstAmount,
            cgst: cgst,
            sgst: sgst,
            totalWithGst: totalWithGst,
            netPrice: amount
        });
    };

    // Handle basic calculator calculation
    const handleBasicCalculate = () => {
        const errors = {};

        // Validate both numbers if expression is empty
        if (!basicData.expression) {
            const num1Error = validatePositiveNumber(basicData.num1, 'First number');
            if (num1Error) errors.num1 = num1Error;

            const num2Error = validatePositiveNumber(basicData.num2, 'Second number');
            if (num2Error) errors.num2 = num2Error;

            // Special validation for division
            if (basicData.operation === '/' && !errors.num2) {
                const num2 = parseFloat(basicData.num2);
                if (num2 === 0) {
                    errors.num2 = 'Cannot divide by zero';
                }
            }
        } else {
            // Validate expression
            if (!basicData.expression.trim()) {
                errors.expression = 'Please enter a valid expression';
            }
        }

        setBasicErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        let result;
        let expression;

        if (basicData.expression) {
            const expr = basicData.expression.trim();
            try {
                result = eval(expr.replace(/×/g, '*').replace(/÷/g, '/'));
                if (!isNaN(result) && isFinite(result)) {
                    expression = expr;
                } else {
                    setBasicErrors({ expression: 'Invalid calculation expression' });
                    return;
                }
            } catch (e) {
                setBasicErrors({ expression: 'Invalid calculation expression' });
                return;
            }
        } else {
            const num1 = parseFloat(basicData.num1);
            const num2 = parseFloat(basicData.num2);

            switch (basicData.operation) {
                case '+': result = num1 + num2; break;
                case '-': result = num1 - num2; break;
                case '*': result = num1 * num2; break;
                case '/': result = num1 / num2; break;
                default: result = 0;
            }

            expression = `${num1} ${basicData.operation} ${num2}`;
        }

        setBasicResult({
            expression: expression,
            result: result
        });
    };

    // Handle tip calculation
    const handleTipCalculate = () => {
        const errors = {};

        // Validate bill amount
        const billError = validatePositiveNumber(tipData.billAmount, 'Bill amount');
        if (billError) errors.billAmount = billError;

        // Validate tip percentage
        let tipPercentage = tipData.tipPercentage;
        if (tipPercentage === 'custom') {
            const tipError = validatePercentage(tipData.customTip, 'Tip percentage');
            if (tipError) errors.customTip = tipError;
            tipPercentage = tipData.customTip;
        } else {
            const tipError = validatePercentage(tipPercentage, 'Tip percentage');
            if (tipError) errors.tipPercentage = tipError;
        }

        // Validate split count
        const splitError = validatePositiveNumber(tipData.splitCount, 'Number of people');
        if (splitError) errors.splitCount = splitError;

        setTipErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        const billAmount = parseFloat(tipData.billAmount);
        const tipPercent = parseFloat(tipPercentage);
        const splitCount = parseInt(tipData.splitCount);

        const tipAmount = (billAmount * tipPercent) / 100;
        const totalWithTip = billAmount + tipAmount;
        const perPerson = totalWithTip / splitCount;

        setTipResult({
            billAmount: billAmount,
            tipPercentage: tipPercent,
            tipAmount: tipAmount,
            totalWithTip: totalWithTip,
            perPerson: perPerson,
            splitCount: splitCount
        });
    };

    // Handle currency conversion
    const handleCurrencyConvert = () => {
        const errors = {};

        // Validate amount
        const amountError = validatePositiveNumber(currencyData.amount, 'Amount');
        if (amountError) errors.amount = amountError;

        setCurrencyErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        const amount = parseFloat(currencyData.amount);

        // Exchange rates
        const exchangeRates = {
            INR: { USD: 0.012, EUR: 0.011, GBP: 0.0095, JPY: 1.79, AUD: 0.018, CAD: 0.016 },
            USD: { INR: 83.0, EUR: 0.92, GBP: 0.79, JPY: 149, AUD: 1.52, CAD: 1.36 },
            EUR: { INR: 90.0, USD: 1.08, GBP: 0.86, JPY: 161, AUD: 1.65, CAD: 1.48 },
            GBP: { INR: 105.0, USD: 1.26, EUR: 1.16, JPY: 188, AUD: 1.92, CAD: 1.72 },
            JPY: { INR: 0.56, USD: 0.0067, EUR: 0.0062, GBP: 0.0053, AUD: 0.010, CAD: 0.009 },
            AUD: { INR: 55.0, USD: 0.66, EUR: 0.61, GBP: 0.52, JPY: 98, CAD: 0.89 },
            CAD: { INR: 61.0, USD: 0.73, EUR: 0.68, GBP: 0.58, JPY: 109, AUD: 1.12 }
        };

        const from = currencyData.fromCurrency;
        const to = currencyData.toCurrency;

        let convertedAmount;
        if (from === to) {
            convertedAmount = amount;
        } else if (exchangeRates[from] && exchangeRates[from][to]) {
            convertedAmount = amount * exchangeRates[from][to];
        } else if (exchangeRates[to] && exchangeRates[to][from]) {
            convertedAmount = amount / exchangeRates[to][from];
        } else {
            // Fallback: convert via USD
            const toUSD = from === 'USD' ? amount : amount * (exchangeRates[from]?.USD || 1);
            convertedAmount = to === 'USD' ? toUSD : toUSD * (exchangeRates.USD?.[to] || 1);
        }

        setCurrencyResult({
            originalAmount: amount,
            fromCurrency: from,
            toCurrency: to,
            convertedAmount: convertedAmount
        });
    };

    // Handle unit conversion
    const handleUnitConvert = () => {
        const errors = {};

        // Validate value
        const valueError = validatePositiveNumber(unitData.value, 'Value');
        if (valueError) errors.value = valueError;

        setUnitErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        const value = parseFloat(unitData.value);

        // Conversion factors
        const conversions = {
            length: {
                meter: 1,
                kilometer: 0.001,
                centimeter: 100,
                millimeter: 1000,
                inch: 39.3701,
                foot: 3.28084,
                yard: 1.09361,
                mile: 0.000621371
            },
            weight: {
                kilogram: 1,
                gram: 1000,
                pound: 2.20462,
                ounce: 35.274
            },
            temperature: {
                celsius: 1,
                fahrenheit: 1,
                kelvin: 1
            }
        };

        const { category, fromUnit, toUnit } = unitData;
        let result;

        if (category === 'temperature') {
            // Temperature conversions
            if (fromUnit === 'celsius' && toUnit === 'fahrenheit') {
                result = (value * 9 / 5) + 32;
            } else if (fromUnit === 'celsius' && toUnit === 'kelvin') {
                result = value + 273.15;
            } else if (fromUnit === 'fahrenheit' && toUnit === 'celsius') {
                result = (value - 32) * 5 / 9;
            } else if (fromUnit === 'fahrenheit' && toUnit === 'kelvin') {
                result = (value - 32) * 5 / 9 + 273.15;
            } else if (fromUnit === 'kelvin' && toUnit === 'celsius') {
                result = value - 273.15;
            } else if (fromUnit === 'kelvin' && toUnit === 'fahrenheit') {
                result = (value - 273.15) * 9 / 5 + 32;
            } else {
                result = value;
            }
        } else {
            // Other conversions
            const fromFactor = conversions[category]?.[fromUnit] || 1;
            const toFactor = conversions[category]?.[toUnit] || 1;

            if (fromFactor && toFactor) {
                // Convert to base unit first, then to target unit
                const baseValue = value / fromFactor;
                result = baseValue * toFactor;
            } else {
                result = value;
            }
        }

        setUnitResult({
            originalValue: value,
            fromUnit: fromUnit,
            toUnit: toUnit,
            convertedValue: result,
            category: category
        });
    };

    // Handle discount calculation
    const handleDiscountCalculate = () => {
        const errors = {};

        // Validate original price
        const priceError = validatePositiveNumber(discountData.originalPrice, 'Original price');
        if (priceError) errors.originalPrice = priceError;

        // Check if at least one discount input is provided
        const hasDiscountPercent = discountData.discountPercent && discountData.discountPercent.trim() !== '';
        const hasDiscountAmount = discountData.discountAmount && discountData.discountAmount.trim() !== '';

        if (!hasDiscountPercent && !hasDiscountAmount) {
            errors.discount = 'Please enter either discount percentage or amount';
        } else if (hasDiscountPercent) {
            const percentError = validatePercentage(discountData.discountPercent, 'Discount percentage');
            if (percentError) errors.discountPercent = percentError;
        } else if (hasDiscountAmount) {
            const amountError = validatePositiveNumber(discountData.discountAmount, 'Discount amount');
            if (amountError) errors.discountAmount = amountError;
        }

        setDiscountErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        const originalPrice = parseFloat(discountData.originalPrice);
        let discountPercent, discountAmount;

        if (hasDiscountPercent) {
            discountPercent = parseFloat(discountData.discountPercent);
            discountAmount = (originalPrice * discountPercent) / 100;
        } else {
            discountAmount = parseFloat(discountData.discountAmount);
            discountPercent = (discountAmount / originalPrice) * 100;
        }

        const finalPrice = originalPrice - discountAmount;
        const savedAmount = discountAmount;
        const savingsPercentage = (discountAmount / originalPrice) * 100;

        setDiscountResult({
            originalPrice: originalPrice,
            discountPercent: discountPercent,
            discountAmount: discountAmount,
            finalPrice: finalPrice,
            savedAmount: savedAmount,
            savingsPercentage: savingsPercentage
        });
    };

    // Handle loan calculation
    const handleLoanCalculate = () => {
        const errors = {};

        // Validate principal
        const principalError = validatePositiveNumber(loanData.principal, 'Loan amount');
        if (principalError) errors.principal = principalError;

        // Validate interest rate
        const rateError = validatePositiveNumber(loanData.interestRate, 'Interest rate');
        if (rateError) errors.interestRate = rateError;

        // Validate loan term
        const termError = validatePositiveNumber(loanData.loanTerm, 'Loan term');
        if (termError) errors.loanTerm = termError;

        setLoanErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        const principal = parseFloat(loanData.principal);
        const annualRate = parseFloat(loanData.interestRate);
        let loanTerm = parseFloat(loanData.loanTerm);

        // Convert years to months if term is in years
        if (loanData.termType === 'years') {
            loanTerm = loanTerm * 12;
        }

        const monthlyRate = annualRate / 100 / 12;

        // Calculate monthly payment using loan formula
        let monthlyPayment;
        if (monthlyRate === 0) {
            monthlyPayment = principal / loanTerm;
        } else {
            monthlyPayment = principal * monthlyRate * Math.pow(1 + monthlyRate, loanTerm) /
                (Math.pow(1 + monthlyRate, loanTerm) - 1);
        }

        const totalPayment = monthlyPayment * loanTerm;
        const totalInterest = totalPayment - principal;
        const interestPercentage = (totalInterest / principal) * 100;

        setLoanResult({
            principal: principal,
            annualRate: annualRate,
            loanTerm: loanTerm,
            monthlyPayment: monthlyPayment,
            totalPayment: totalPayment,
            totalInterest: totalInterest,
            interestPercentage: interestPercentage
        });
    };

    // Handle BMI calculation
    const handleBmiCalculate = () => {
        const errors = {};

        // Validate height
        const heightError = validatePositiveNumber(bmiData.height, 'Height');
        if (heightError) errors.height = heightError;

        // Validate weight
        const weightError = validatePositiveNumber(bmiData.weight, 'Weight');
        if (weightError) errors.weight = weightError;

        setBmiErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        let height = parseFloat(bmiData.height);
        let weight = parseFloat(bmiData.weight);

        // Convert to meters and kilograms if needed
        if (bmiData.heightUnit === 'cm') {
            height = height / 100; // cm to meters
        } else if (bmiData.heightUnit === 'feet') {
            height = height * 0.3048; // feet to meters
        }

        if (bmiData.weightUnit === 'pounds') {
            weight = weight * 0.453592; // pounds to kg
        }

        const bmi = weight / (height * height);

        // Determine BMI category
        let category, categoryColor;
        if (bmi < 18.5) {
            category = 'Underweight';
            categoryColor = '#3498db';
        } else if (bmi < 25) {
            category = 'Normal weight';
            categoryColor = '#2ecc71';
        } else if (bmi < 30) {
            category = 'Overweight';
            categoryColor = '#f39c12';
        } else {
            category = 'Obese';
            categoryColor = '#e74c3c';
        }

        setBmiResult({
            bmi: bmi,
            category: category,
            categoryColor: categoryColor,
            height: height * 100, // in cm for display
            weight: weight // in kg for display
        });
    };

    // Reset calculator
    const handleReset = () => {
        switch (activeCalculator) {
            case 'gst':
                setGstData({ amount: '', rate: '', customRate: '' }); // BLANK
                setGstResult(null);
                setGstErrors({});
                break;
            case 'basic':
                setBasicData({ expression: '', num1: '', num2: '', operation: '+' });
                setBasicResult(null);
                setBasicErrors({});
                break;
            case 'tip':
                setTipData({ billAmount: '', tipPercentage: '15', customTip: '', splitCount: '1' });
                setTipResult(null);
                setTipErrors({});
                break;
            case 'currency':
                setCurrencyData({ amount: '', fromCurrency: 'INR', toCurrency: 'USD' });
                setCurrencyResult(null);
                setCurrencyErrors({});
                break;
            case 'unit':
                setUnitData({ value: '', fromUnit: 'meter', toUnit: 'kilometer', category: 'length' });
                setUnitResult(null);
                setUnitErrors({});
                break;
            case 'discount':
                setDiscountData({ originalPrice: '', discountPercent: '', discountAmount: '' });
                setDiscountResult(null);
                setDiscountErrors({});
                break;
            case 'loan':
                setLoanData({ principal: '', interestRate: '', loanTerm: '', termType: 'years' });
                setLoanResult(null);
                setLoanErrors({});
                break;
            case 'bmi':
                setBmiData({ height: '', weight: '', heightUnit: 'cm', weightUnit: 'kg' });
                setBmiResult(null);
                setBmiErrors({});
                break;
            default:
                break;
        }
    };

    // Get icon class for calculator type
    const getIconClass = (iconName) => {
        return `fas fa-${iconName}`;
    };

    // Get active calculator name
    const getActiveCalculatorName = () => {
        const active = calculatorTypes.find(calc => calc.id === activeCalculator);
        return active ? active.name : 'Calculator';
    };

    return (
        <div className="calc-main-container">
            <div className="calc-main-header">
                <div className="calc-header-content">
                    <h1 className="calc-title"><i className="fas fa-calculator"></i> Pro Calculator Suite</h1>
                    <p className="calc-subtitle">All-in-one calculation solution with side-by-side layout</p>
                </div>
            </div>

            <div className="calc-dashboard">
                {/* Mobile Dropdown */}
                {isMobile && (
                    <div className="calc-mobile-selector">
                        <button
                            className="calc-mobile-dropdown-btn"
                            onClick={() => setShowDropdown(!showDropdown)}
                        >
                            <i className={getIconClass(calculatorTypes.find(c => c.id === activeCalculator)?.icon || 'calculator')}></i>
                            {getActiveCalculatorName()}
                            <i className={`fas fa-chevron-${showDropdown ? 'up' : 'down'}`}></i>
                        </button>

                        {showDropdown && (
                            <div className="calc-mobile-dropdown-menu">
                                {calculatorTypes.map((type) => (
                                    <button
                                        key={type.id}
                                        className={`calc-mobile-dropdown-item ${activeCalculator === type.id ? 'calc-mobile-dropdown-active' : ''}`}
                                        onClick={() => handleCalculatorSelect(type.id)}
                                    >
                                        <i className={getIconClass(type.icon)}></i>
                                        {type.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Desktop Calculator Types */}
                {!isMobile && (
                    <div className="calc-types-grid">
                        {calculatorTypes.map((type) => (
                            <button
                                key={type.id}
                                className={`calc-type-button ${activeCalculator === type.id ? 'calc-type-active' : ''}`}
                                onClick={() => handleCalculatorSelect(type.id)}
                            >
                                <i className={getIconClass(type.icon)}></i>
                                <span className="calc-type-name">{type.name}</span>
                                {activeCalculator === type.id && (
                                    <span className="calc-active-indicator"></span>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                <div className="calc-content-area">
                    {/* GST Calculator */}
                    {activeCalculator === 'gst' && (
                        <div className="calc-wrapper calc-active">
                            <div className="calc-header-wrapper">
                                <h2 className="calc-header-title"><i className="fas fa-receipt"></i> GST Calculator</h2>
                            </div>

                            <div className="calc-side-layout">
                                {/* Left Side - Inputs */}
                                <div className="calc-input-panel">
                                    <div className="calc-input-group">
                                        <label htmlFor="gst-amount" className="calc-input-label">
                                            <i className="fas fa-rupee-sign"></i> Original Amount (₹)
                                        </label>
                                        <input
                                            type="number"
                                            id="gst-amount"
                                            className={`calc-input-field ${gstErrors.amount ? 'calc-input-error' : ''}`}
                                            placeholder="Enter amount"
                                            value={gstData.amount}
                                            onChange={(e) => setGstData({ ...gstData, amount: e.target.value })}
                                            min="0"
                                            step="0.01"
                                        />
                                        {gstErrors.amount && <div className="calc-error-message">{gstErrors.amount}</div>}
                                    </div>

                                    <div className="calc-input-group">
                                        <label htmlFor="gst-rate" className="calc-input-label">
                                            <i className="fas fa-percentage"></i> GST Rate (%)
                                        </label>
                                        <select
                                            id="gst-rate"
                                            className={`calc-select-field ${gstErrors.rate ? 'calc-input-error' : ''}`}
                                            value={gstData.rate}
                                            onChange={(e) => setGstData({ ...gstData, rate: e.target.value })}
                                        >
                                            <option value="" disabled>Select GST Rate</option>
                                            {/* <option value="0">0%</option> */}
                                            <option value="5">5%</option>
                                            <option value="12">12%</option>
                                            <option value="18">18%</option>
                                            <option value="28">28%</option>
                                            <option value="custom">Custom</option>
                                        </select>
                                        {gstErrors.rate && <div className="calc-error-message">{gstErrors.rate}</div>}
                                    </div>

                                    {gstData.rate === 'custom' && (
                                        <div className="calc-input-group">
                                            <label htmlFor="custom-gst-rate" className="calc-input-label">
                                                <i className="fas fa-sliders-h"></i> Custom GST Rate (%)
                                            </label>
                                            <input
                                                type="number"
                                                id="custom-gst-rate"
                                                className={`calc-input-field ${gstErrors.customRate ? 'calc-input-error' : ''}`}
                                                placeholder="Enter custom rate"
                                                value={gstData.customRate}
                                                onChange={(e) => setGstData({ ...gstData, customRate: e.target.value })}
                                                min="0"
                                                max="100"
                                                step="0.1"
                                            />
                                            {gstErrors.customRate && <div className="calc-error-message">{gstErrors.customRate}</div>}
                                        </div>
                                    )}

                                    <div className="calc-button-group">
                                        <button className="calc-btn calc-btn-primary" onClick={handleGstCalculate}>
                                            <i className="fas fa-calculator"></i> Calculate GST
                                        </button>
                                        <button className="calc-btn calc-btn-secondary" onClick={handleReset}>
                                            <i className="fas fa-redo"></i> Reset
                                        </button>
                                    </div>
                                </div>

                                {/* Right Side - Results */}
                                <div className="calc-result-panel">
                                    {gstResult ? (
                                        <div className="calc-result-content">
                                            <div className="calc-result-header">
                                                <h3 className="calc-result-title"><i className="fas fa-chart-pie"></i> GST Calculation</h3>
                                            </div>

                                            <div className="calc-result-summary">
                                                <div className="calc-summary-card calc-summary-main">
                                                    <div className="calc-summary-label">Total Amount</div>
                                                    <div className="calc-summary-value">₹{gstResult.totalWithGst.toFixed(2)}</div>
                                                </div>
                                            </div>

                                            <div className="calc-result-details">
                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-box"></i> Net Price
                                                    </div>
                                                    <div className="calc-detail-value">₹{gstResult.originalAmount.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-percentage"></i> GST Rate
                                                    </div>
                                                    <div className="calc-detail-value">{gstResult.gstRate}%</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-receipt"></i> GST Amount
                                                    </div>
                                                    <div className="calc-detail-value">₹{gstResult.gstAmount.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-balance-scale-left"></i> CGST
                                                    </div>
                                                    <div className="calc-detail-value">₹{gstResult.cgst.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-balance-scale-right"></i> SGST
                                                    </div>
                                                    <div className="calc-detail-value">₹{gstResult.sgst.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-detail-row calc-detail-highlight">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-file-invoice-dollar"></i> Total Payable
                                                    </div>
                                                    <div className="calc-detail-value">₹{gstResult.totalWithGst.toFixed(2)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="calc-empty-result">
                                            <i className="fas fa-calculator"></i>
                                            <p>Enter values and click Calculate to see results</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Basic Calculator */}
                    {activeCalculator === 'basic' && (
                        <div className="calc-wrapper calc-active">
                            <div className="calc-header-wrapper">
                                <h2 className="calc-header-title"><i className="fas fa-calculator"></i> Basic Calculator</h2>
                            </div>

                            <div className="calc-side-layout">
                                {/* Left Side - Inputs */}
                                <div className="calc-input-panel">
                                    <div className="calc-input-group">
                                        <label htmlFor="basic-expression" className="calc-input-label">
                                            <i className="fas fa-bolt"></i> Quick Expression (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            id="basic-expression"
                                            className={`calc-input-field ${basicErrors.expression ? 'calc-input-error' : ''}`}
                                            placeholder="15 + 27, 45 * 3, 100 / 4"
                                            value={basicData.expression}
                                            onChange={(e) => setBasicData({ ...basicData, expression: e.target.value })}
                                        />
                                        {basicErrors.expression && <div className="calc-error-message">{basicErrors.expression}</div>}
                                    </div>

                                    <div className="calc-divider">
                                        <span>OR</span>
                                    </div>

                                    <div className="calc-input-row">
                                        <div className="calc-input-group">
                                            <label htmlFor="num1" className="calc-input-label">
                                                <i className="fas fa-hashtag"></i> First Number
                                            </label>
                                            <input
                                                type="number"
                                                id="num1"
                                                className={`calc-input-field ${basicErrors.num1 ? 'calc-input-error' : ''}`}
                                                placeholder="Enter first number"
                                                value={basicData.num1}
                                                onChange={(e) => setBasicData({ ...basicData, num1: e.target.value })}
                                                step="0.01"
                                            />
                                            {basicErrors.num1 && <div className="calc-error-message">{basicErrors.num1}</div>}
                                        </div>

                                        <div className="calc-input-group">
                                            <label htmlFor="operation" className="calc-input-label">
                                                <i className="fas fa-plus-minus"></i> Operation
                                            </label>
                                            <select
                                                id="operation"
                                                className="calc-select-field"
                                                value={basicData.operation}
                                                onChange={(e) => setBasicData({ ...basicData, operation: e.target.value })}
                                            >
                                                <option value="+">Addition (+)</option>
                                                <option value="-">Subtraction (-)</option>
                                                <option value="*">Multiplication (×)</option>
                                                <option value="/">Division (÷)</option>
                                            </select>
                                        </div>

                                        <div className="calc-input-group">
                                            <label htmlFor="num2" className="calc-input-label">
                                                <i className="fas fa-hashtag"></i> Second Number
                                            </label>
                                            <input
                                                type="number"
                                                id="num2"
                                                className={`calc-input-field ${basicErrors.num2 ? 'calc-input-error' : ''}`}
                                                placeholder="Enter second number"
                                                value={basicData.num2}
                                                onChange={(e) => setBasicData({ ...basicData, num2: e.target.value })}
                                                step="0.01"
                                            />
                                            {basicErrors.num2 && <div className="calc-error-message">{basicErrors.num2}</div>}
                                        </div>
                                    </div>

                                    <div className="calc-button-group">
                                        <button className="calc-btn calc-btn-primary" onClick={handleBasicCalculate}>
                                            <i className="fas fa-calculator"></i> Calculate
                                        </button>
                                        <button className="calc-btn calc-btn-secondary" onClick={handleReset}>
                                            <i className="fas fa-redo"></i> Reset
                                        </button>
                                    </div>
                                </div>

                                {/* Right Side - Results */}
                                <div className="calc-result-panel">
                                    {basicResult ? (
                                        <div className="calc-result-content">
                                            <div className="calc-result-header">
                                                <h3 className="calc-result-title"><i className="fas fa-chart-bar"></i> Calculation Result</h3>
                                            </div>

                                            <div className="calc-result-summary">
                                                <div className="calc-summary-card calc-summary-main">
                                                    <div className="calc-summary-label">Result</div>
                                                    <div className="calc-summary-value">
                                                        {typeof basicResult.result === 'number' ?
                                                            basicResult.result.toLocaleString('en-IN', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2
                                                            }) :
                                                            basicResult.result
                                                        }
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="calc-result-details">
                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-code"></i> Expression
                                                    </div>
                                                    <div className="calc-detail-value">{basicResult.expression}</div>
                                                </div>

                                                <div className="calc-detail-row calc-detail-highlight">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-equals"></i> Final Result
                                                    </div>
                                                    <div className="calc-detail-value">
                                                        {typeof basicResult.result === 'number' ?
                                                            basicResult.result.toLocaleString('en-IN', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2
                                                            }) :
                                                            basicResult.result
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="calc-empty-result">
                                            <i className="fas fa-calculator"></i>
                                            <p>Enter values and click Calculate to see results</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tip Calculator */}
                    {activeCalculator === 'tip' && (
                        <div className="calc-wrapper calc-active">
                            <div className="calc-header-wrapper">
                                <h2 className="calc-header-title"><i className="fas fa-utensils"></i> Tip Calculator</h2>
                            </div>

                            <div className="calc-side-layout">
                                {/* Left Side - Inputs */}
                                <div className="calc-input-panel">
                                    <div className="calc-input-group">
                                        <label htmlFor="bill-amount" className="calc-input-label">
                                            <i className="fas fa-rupee-sign"></i> Bill Amount (₹)
                                        </label>
                                        <input
                                            type="number"
                                            id="bill-amount"
                                            className={`calc-input-field ${tipErrors.billAmount ? 'calc-input-error' : ''}`}
                                            placeholder="Enter bill amount"
                                            value={tipData.billAmount}
                                            onChange={(e) => setTipData({ ...tipData, billAmount: e.target.value })}
                                            min="0"
                                            step="0.01"
                                        />
                                        {tipErrors.billAmount && <div className="calc-error-message">{tipErrors.billAmount}</div>}
                                    </div>

                                    <div className="calc-input-group">
                                        <label htmlFor="tip-percentage" className="calc-input-label">
                                            <i className="fas fa-percentage"></i> Tip Percentage (%)
                                        </label>
                                        <select
                                            id="tip-percentage"
                                            className={`calc-select-field ${tipErrors.tipPercentage ? 'calc-input-error' : ''}`}
                                            value={tipData.tipPercentage}
                                            onChange={(e) => setTipData({ ...tipData, tipPercentage: e.target.value })}
                                        >
                                            <option value="10">10%</option>
                                            <option value="15">15%</option>
                                            <option value="18">18%</option>
                                            <option value="20">20%</option>
                                            <option value="custom">Custom</option>
                                        </select>
                                        {tipErrors.tipPercentage && <div className="calc-error-message">{tipErrors.tipPercentage}</div>}
                                    </div>

                                    {tipData.tipPercentage === 'custom' && (
                                        <div className="calc-input-group">
                                            <label htmlFor="custom-tip" className="calc-input-label">
                                                <i className="fas fa-sliders-h"></i> Custom Tip (%)
                                            </label>
                                            <input
                                                type="number"
                                                id="custom-tip"
                                                className={`calc-input-field ${tipErrors.customTip ? 'calc-input-error' : ''}`}
                                                placeholder="Enter custom tip"
                                                value={tipData.customTip}
                                                onChange={(e) => setTipData({ ...tipData, customTip: e.target.value })}
                                                min="0"
                                                max="100"
                                                step="0.1"
                                            />
                                            {tipErrors.customTip && <div className="calc-error-message">{tipErrors.customTip}</div>}
                                        </div>
                                    )}

                                    <div className="calc-input-group">
                                        <label htmlFor="split-count" className="calc-input-label">
                                            <i className="fas fa-users"></i> Split Between
                                        </label>
                                        <input
                                            type="number"
                                            id="split-count"
                                            className={`calc-input-field ${tipErrors.splitCount ? 'calc-input-error' : ''}`}
                                            value={tipData.splitCount}
                                            min="1"
                                            onChange={(e) => setTipData({ ...tipData, splitCount: e.target.value })}
                                        />
                                        {tipErrors.splitCount && <div className="calc-error-message">{tipErrors.splitCount}</div>}
                                    </div>

                                    <div className="calc-button-group">
                                        <button className="calc-btn calc-btn-primary" onClick={handleTipCalculate}>
                                            <i className="fas fa-calculator"></i> Calculate Tip
                                        </button>
                                        <button className="calc-btn calc-btn-secondary" onClick={handleReset}>
                                            <i className="fas fa-redo"></i> Reset
                                        </button>
                                    </div>
                                </div>

                                {/* Right Side - Results */}
                                <div className="calc-result-panel">
                                    {tipResult ? (
                                        <div className="calc-result-content">
                                            <div className="calc-result-header">
                                                <h3 className="calc-result-title"><i className="fas fa-chart-pie"></i> Tip Calculation</h3>
                                            </div>

                                            <div className="calc-result-summary">
                                                <div className="calc-summary-card calc-summary-main">
                                                    <div className="calc-summary-label">Total with Tip</div>
                                                    <div className="calc-summary-value">₹{tipResult.totalWithTip.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-summary-card">
                                                    <div className="calc-summary-label">Per Person</div>
                                                    <div className="calc-summary-value">₹{tipResult.perPerson.toFixed(2)}</div>
                                                </div>
                                            </div>

                                            <div className="calc-result-details">
                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-receipt"></i> Bill Amount
                                                    </div>
                                                    <div className="calc-detail-value">₹{tipResult.billAmount.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-percentage"></i> Tip Percentage
                                                    </div>
                                                    <div className="calc-detail-value">{tipResult.tipPercentage}%</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-money-bill-wave"></i> Tip Amount
                                                    </div>
                                                    <div className="calc-detail-value">₹{tipResult.tipAmount.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-user-friends"></i> People
                                                    </div>
                                                    <div className="calc-detail-value">{tipResult.splitCount}</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-user"></i> Per Person
                                                    </div>
                                                    <div className="calc-detail-value">₹{tipResult.perPerson.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-detail-row calc-detail-highlight">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-file-invoice-dollar"></i> Total Payable
                                                    </div>
                                                    <div className="calc-detail-value">₹{tipResult.totalWithTip.toFixed(2)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="calc-empty-result">
                                            <i className="fas fa-utensils"></i>
                                            <p>Enter bill details to calculate tip</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Currency Converter */}
                    {activeCalculator === 'currency' && (
                        <div className="calc-wrapper calc-active">
                            <div className="calc-header-wrapper">
                                <h2 className="calc-header-title"><i className="fas fa-money-bill-wave"></i> Currency Converter</h2>
                            </div>

                            <div className="calc-side-layout">
                                {/* Left Side - Inputs */}
                                <div className="calc-input-panel">
                                    <div className="calc-input-group">
                                        <label htmlFor="currency-amount" className="calc-input-label">
                                            <i className="fas fa-money-check-alt"></i> Amount
                                        </label>
                                        <input
                                            type="number"
                                            id="currency-amount"
                                            className={`calc-input-field ${currencyErrors.amount ? 'calc-input-error' : ''}`}
                                            placeholder="Enter amount"
                                            value={currencyData.amount}
                                            onChange={(e) => setCurrencyData({ ...currencyData, amount: e.target.value })}
                                            min="0"
                                            step="0.01"
                                        />
                                        {currencyErrors.amount && <div className="calc-error-message">{currencyErrors.amount}</div>}
                                    </div>

                                    <div className="calc-input-row">
                                        <div className="calc-input-group">
                                            <label htmlFor="from-currency" className="calc-input-label">
                                                <i className="fas fa-arrow-right"></i> From Currency
                                            </label>
                                            <select
                                                id="from-currency"
                                                className="calc-select-field"
                                                value={currencyData.fromCurrency}
                                                onChange={(e) => setCurrencyData({ ...currencyData, fromCurrency: e.target.value })}
                                            >
                                                <option value="INR">Indian Rupee (₹)</option>
                                                <option value="USD">US Dollar ($)</option>
                                                <option value="EUR">Euro (€)</option>
                                                <option value="GBP">British Pound (£)</option>
                                                <option value="JPY">Japanese Yen (¥)</option>
                                            </select>
                                        </div>

                                        <div className="calc-input-group">
                                            <label htmlFor="to-currency" className="calc-input-label">
                                                <i className="fas fa-arrow-left"></i> To Currency
                                            </label>
                                            <select
                                                id="to-currency"
                                                className="calc-select-field"
                                                value={currencyData.toCurrency}
                                                onChange={(e) => setCurrencyData({ ...currencyData, toCurrency: e.target.value })}
                                            >
                                                <option value="USD">US Dollar ($)</option>
                                                <option value="INR">Indian Rupee (₹)</option>
                                                <option value="EUR">Euro (€)</option>
                                                <option value="GBP">British Pound (£)</option>
                                                <option value="JPY">Japanese Yen (¥)</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="calc-button-group">
                                        <button className="calc-btn calc-btn-primary" onClick={handleCurrencyConvert}>
                                            <i className="fas fa-exchange-alt"></i> Convert
                                        </button>
                                        <button className="calc-btn calc-btn-secondary" onClick={handleReset}>
                                            <i className="fas fa-redo"></i> Reset
                                        </button>
                                    </div>
                                </div>

                                {/* Right Side - Results */}
                                <div className="calc-result-panel">
                                    {currencyResult ? (
                                        <div className="calc-result-content">
                                            <div className="calc-result-header">
                                                <h3 className="calc-result-title"><i className="fas fa-chart-line"></i> Currency Conversion</h3>
                                            </div>

                                            <div className="calc-result-summary">
                                                <div className="calc-summary-card calc-summary-main">
                                                    <div className="calc-summary-label">Converted Amount</div>
                                                    <div className="calc-summary-value">
                                                        {currencyResult.convertedAmount.toLocaleString('en-IN', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })} {currencyResult.toCurrency}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="calc-result-details">
                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-money-bill"></i> Original Amount
                                                    </div>
                                                    <div className="calc-detail-value">
                                                        {currencyResult.originalAmount.toLocaleString('en-IN', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })} {currencyResult.fromCurrency}
                                                    </div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-exchange-alt"></i> Exchange Rate
                                                    </div>
                                                    <div className="calc-detail-value">
                                                        1 {currencyResult.fromCurrency} = {(currencyResult.convertedAmount / currencyResult.originalAmount).toFixed(4)} {currencyResult.toCurrency}
                                                    </div>
                                                </div>

                                                <div className="calc-detail-row calc-detail-highlight">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-money-check-alt"></i> Final Amount
                                                    </div>
                                                    <div className="calc-detail-value">
                                                        {currencyResult.convertedAmount.toLocaleString('en-IN', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })} {currencyResult.toCurrency}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="calc-empty-result">
                                            <i className="fas fa-money-bill-wave"></i>
                                            <p>Enter amount to convert currencies</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Unit Converter */}
                    {activeCalculator === 'unit' && (
                        <div className="calc-wrapper calc-active">
                            <div className="calc-header-wrapper">
                                <h2 className="calc-header-title"><i className="fas fa-ruler"></i> Unit Converter</h2>
                            </div>

                            <div className="calc-side-layout">
                                {/* Left Side - Inputs */}
                                <div className="calc-input-panel">
                                    <div className="calc-input-group">
                                        <label htmlFor="unit-category" className="calc-input-label">
                                            <i className="fas fa-filter"></i> Category
                                        </label>
                                        <select
                                            id="unit-category"
                                            className="calc-select-field"
                                            value={unitData.category}
                                            onChange={(e) => {
                                                const category = e.target.value;
                                                let fromUnit, toUnit;

                                                if (category === 'length') {
                                                    fromUnit = 'meter';
                                                    toUnit = 'kilometer';
                                                } else if (category === 'weight') {
                                                    fromUnit = 'kilogram';
                                                    toUnit = 'gram';
                                                } else {
                                                    fromUnit = 'celsius';
                                                    toUnit = 'fahrenheit';
                                                }

                                                setUnitData({
                                                    ...unitData,
                                                    category: category,
                                                    fromUnit: fromUnit,
                                                    toUnit: toUnit
                                                });
                                            }}
                                        >
                                            <option value="length">Length</option>
                                            <option value="weight">Weight</option>
                                            <option value="temperature">Temperature</option>
                                        </select>
                                    </div>

                                    <div className="calc-input-group">
                                        <label htmlFor="unit-value" className="calc-input-label">
                                            <i className="fas fa-hashtag"></i> Value
                                        </label>
                                        <input
                                            type="number"
                                            id="unit-value"
                                            className={`calc-input-field ${unitErrors.value ? 'calc-input-error' : ''}`}
                                            placeholder="Enter value"
                                            value={unitData.value}
                                            onChange={(e) => setUnitData({ ...unitData, value: e.target.value })}
                                            step="0.01"
                                        />
                                        {unitErrors.value && <div className="calc-error-message">{unitErrors.value}</div>}
                                    </div>

                                    <div className="calc-input-row">
                                        <div className="calc-input-group">
                                            <label htmlFor="from-unit" className="calc-input-label">
                                                <i className="fas fa-arrow-right"></i> From Unit
                                            </label>
                                            <select
                                                id="from-unit"
                                                className="calc-select-field"
                                                value={unitData.fromUnit}
                                                onChange={(e) => setUnitData({ ...unitData, fromUnit: e.target.value })}
                                            >
                                                {unitData.category === 'length' && (
                                                    <>
                                                        <option value="meter">Meter (m)</option>
                                                        <option value="kilometer">Kilometer (km)</option>
                                                        <option value="centimeter">Centimeter (cm)</option>
                                                        <option value="millimeter">Millimeter (mm)</option>
                                                    </>
                                                )}
                                                {unitData.category === 'weight' && (
                                                    <>
                                                        <option value="kilogram">Kilogram (kg)</option>
                                                        <option value="gram">Gram (g)</option>
                                                        <option value="pound">Pound (lb)</option>
                                                        <option value="ounce">Ounce (oz)</option>
                                                    </>
                                                )}
                                                {unitData.category === 'temperature' && (
                                                    <>
                                                        <option value="celsius">Celsius (°C)</option>
                                                        <option value="fahrenheit">Fahrenheit (°F)</option>
                                                        <option value="kelvin">Kelvin (K)</option>
                                                    </>
                                                )}
                                            </select>
                                        </div>

                                        <div className="calc-input-group">
                                            <label htmlFor="to-unit" className="calc-input-label">
                                                <i className="fas fa-arrow-left"></i> To Unit
                                            </label>
                                            <select
                                                id="to-unit"
                                                className="calc-select-field"
                                                value={unitData.toUnit}
                                                onChange={(e) => setUnitData({ ...unitData, toUnit: e.target.value })}
                                            >
                                                {unitData.category === 'length' && (
                                                    <>
                                                        <option value="meter">Meter (m)</option>
                                                        <option value="kilometer">Kilometer (km)</option>
                                                        <option value="centimeter">Centimeter (cm)</option>
                                                        <option value="millimeter">Millimeter (mm)</option>
                                                    </>
                                                )}
                                                {unitData.category === 'weight' && (
                                                    <>
                                                        <option value="kilogram">Kilogram (kg)</option>
                                                        <option value="gram">Gram (g)</option>
                                                        <option value="pound">Pound (lb)</option>
                                                        <option value="ounce">Ounce (oz)</option>
                                                    </>
                                                )}
                                                {unitData.category === 'temperature' && (
                                                    <>
                                                        <option value="celsius">Celsius (°C)</option>
                                                        <option value="fahrenheit">Fahrenheit (°F)</option>
                                                        <option value="kelvin">Kelvin (K)</option>
                                                    </>
                                                )}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="calc-button-group">
                                        <button className="calc-btn calc-btn-primary" onClick={handleUnitConvert}>
                                            <i className="fas fa-exchange-alt"></i> Convert Units
                                        </button>
                                        <button className="calc-btn calc-btn-secondary" onClick={handleReset}>
                                            <i className="fas fa-redo"></i> Reset
                                        </button>
                                    </div>
                                </div>

                                {/* Right Side - Results */}
                                <div className="calc-result-panel">
                                    {unitResult ? (
                                        <div className="calc-result-content">
                                            <div className="calc-result-header">
                                                <h3 className="calc-result-title"><i className="fas fa-chart-line"></i> Unit Conversion</h3>
                                            </div>

                                            <div className="calc-result-summary">
                                                <div className="calc-summary-card calc-summary-main">
                                                    <div className="calc-summary-label">Converted Value</div>
                                                    <div className="calc-summary-value">
                                                        {unitResult.convertedValue.toLocaleString('en-IN', {
                                                            minimumFractionDigits: 4,
                                                            maximumFractionDigits: 4
                                                        })} {unitResult.toUnit}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="calc-result-details">
                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-hashtag"></i> Original Value
                                                    </div>
                                                    <div className="calc-detail-value">
                                                        {unitResult.originalValue} {unitResult.fromUnit}
                                                    </div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-exchange-alt"></i> Conversion
                                                    </div>
                                                    <div className="calc-detail-value">
                                                        {unitResult.fromUnit} → {unitResult.toUnit}
                                                    </div>
                                                </div>

                                                <div className="calc-detail-row calc-detail-highlight">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-check-circle"></i> Final Value
                                                    </div>
                                                    <div className="calc-detail-value">
                                                        {unitResult.convertedValue.toLocaleString('en-IN', {
                                                            minimumFractionDigits: 4,
                                                            maximumFractionDigits: 4
                                                        })} {unitResult.toUnit}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="calc-empty-result">
                                            <i className="fas fa-ruler"></i>
                                            <p>Enter values to convert units</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Discount Calculator */}
                    {activeCalculator === 'discount' && (
                        <div className="calc-wrapper calc-active">
                            <div className="calc-header-wrapper">
                                <h2 className="calc-header-title"><i className="fas fa-tag"></i> Discount Calculator</h2>
                            </div>

                            <div className="calc-side-layout">
                                {/* Left Side - Inputs */}
                                <div className="calc-input-panel">
                                    <div className="calc-input-group">
                                        <label htmlFor="original-price" className="calc-input-label">
                                            <i className="fas fa-tag"></i> Original Price (₹)
                                        </label>
                                        <input
                                            type="number"
                                            id="original-price"
                                            className={`calc-input-field ${discountErrors.originalPrice ? 'calc-input-error' : ''}`}
                                            placeholder="Enter original price"
                                            value={discountData.originalPrice}
                                            onChange={(e) => setDiscountData({ ...discountData, originalPrice: e.target.value })}
                                            min="0"
                                            step="0.01"
                                        />
                                        {discountErrors.originalPrice && <div className="calc-error-message">{discountErrors.originalPrice}</div>}
                                    </div>

                                    <div className="calc-input-row">
                                        <div className="calc-input-group">
                                            <label htmlFor="discount-percent" className="calc-input-label">
                                                <i className="fas fa-percentage"></i> Discount (%)
                                            </label>
                                            <input
                                                type="number"
                                                id="discount-percent"
                                                className={`calc-input-field ${discountErrors.discountPercent ? 'calc-input-error' : ''}`}
                                                placeholder="Enter discount %"
                                                value={discountData.discountPercent}
                                                onChange={(e) => setDiscountData({ ...discountData, discountPercent: e.target.value })}
                                                min="0"
                                                max="100"
                                                step="0.1"
                                            />
                                            {discountErrors.discountPercent && <div className="calc-error-message">{discountErrors.discountPercent}</div>}
                                        </div>

                                        <div className="calc-input-group">
                                            <label htmlFor="discount-amount" className="calc-input-label">
                                                <i className="fas fa-rupee-sign"></i> OR Discount Amount (₹)
                                            </label>
                                            <input
                                                type="number"
                                                id="discount-amount"
                                                className={`calc-input-field ${discountErrors.discountAmount ? 'calc-input-error' : ''}`}
                                                placeholder="Enter discount amount"
                                                value={discountData.discountAmount}
                                                onChange={(e) => setDiscountData({ ...discountData, discountAmount: e.target.value })}
                                                min="0"
                                                step="0.01"
                                            />
                                            {discountErrors.discountAmount && <div className="calc-error-message">{discountErrors.discountAmount}</div>}
                                        </div>
                                    </div>

                                    <div className="calc-button-group">
                                        <button className="calc-btn calc-btn-primary" onClick={handleDiscountCalculate}>
                                            <i className="fas fa-calculator"></i> Calculate Discount
                                        </button>
                                        <button className="calc-btn calc-btn-secondary" onClick={handleReset}>
                                            <i className="fas fa-redo"></i> Reset
                                        </button>
                                    </div>
                                </div>

                                {/* Right Side - Results */}
                                <div className="calc-result-panel">
                                    {discountResult ? (
                                        <div className="calc-result-content">
                                            <div className="calc-result-header">
                                                <h3 className="calc-result-title"><i className="fas fa-chart-pie"></i> Discount Calculation</h3>
                                            </div>

                                            <div className="calc-result-summary">
                                                <div className="calc-summary-card calc-summary-main">
                                                    <div className="calc-summary-label">Final Price</div>
                                                    <div className="calc-summary-value">₹{discountResult.finalPrice.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-summary-card">
                                                    <div className="calc-summary-label">You Save</div>
                                                    <div className="calc-summary-value">₹{discountResult.savedAmount.toFixed(2)}</div>
                                                </div>
                                            </div>

                                            <div className="calc-result-details">
                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-tag"></i> Original Price
                                                    </div>
                                                    <div className="calc-detail-value">₹{discountResult.originalPrice.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-percentage"></i> Discount %
                                                    </div>
                                                    <div className="calc-detail-value">{discountResult.discountPercent.toFixed(2)}%</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-money-bill-wave"></i> Discount Amount
                                                    </div>
                                                    <div className="calc-detail-value">₹{discountResult.discountAmount.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-wallet"></i> Amount Saved
                                                    </div>
                                                    <div className="calc-detail-value">₹{discountResult.savedAmount.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-detail-row calc-detail-highlight">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-file-invoice-dollar"></i> Final Price
                                                    </div>
                                                    <div className="calc-detail-value">₹{discountResult.finalPrice.toFixed(2)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="calc-empty-result">
                                            <i className="fas fa-tag"></i>
                                            <p>Enter price to calculate discount</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Loan Calculator */}
                    {activeCalculator === 'loan' && (
                        <div className="calc-wrapper calc-active">
                            <div className="calc-header-wrapper">
                                <h2 className="calc-header-title"><i className="fas fa-home"></i> Loan Calculator</h2>
                            </div>

                            <div className="calc-side-layout">
                                {/* Left Side - Inputs */}
                                <div className="calc-input-panel">
                                    <div className="calc-input-group">
                                        <label htmlFor="loan-principal" className="calc-input-label">
                                            <i className="fas fa-rupee-sign"></i> Loan Amount (₹)
                                        </label>
                                        <input
                                            type="number"
                                            id="loan-principal"
                                            className={`calc-input-field ${loanErrors.principal ? 'calc-input-error' : ''}`}
                                            placeholder="Enter loan amount"
                                            value={loanData.principal}
                                            onChange={(e) => setLoanData({ ...loanData, principal: e.target.value })}
                                            min="0"
                                            step="0.01"
                                        />
                                        {loanErrors.principal && <div className="calc-error-message">{loanErrors.principal}</div>}
                                    </div>

                                    <div className="calc-input-row">
                                        <div className="calc-input-group">
                                            <label htmlFor="interest-rate" className="calc-input-label">
                                                <i className="fas fa-percentage"></i> Interest Rate (%)
                                            </label>
                                            <input
                                                type="number"
                                                id="interest-rate"
                                                className={`calc-input-field ${loanErrors.interestRate ? 'calc-input-error' : ''}`}
                                                placeholder="Enter interest rate"
                                                step="0.01"
                                                value={loanData.interestRate}
                                                onChange={(e) => setLoanData({ ...loanData, interestRate: e.target.value })}
                                                min="0"
                                                step="0.01"
                                            />
                                            {loanErrors.interestRate && <div className="calc-error-message">{loanErrors.interestRate}</div>}
                                        </div>

                                        <div className="calc-input-group">
                                            <label htmlFor="loan-term" className="calc-input-label">
                                                <i className="fas fa-calendar-alt"></i> Loan Term
                                            </label>
                                            <input
                                                type="number"
                                                id="loan-term"
                                                className={`calc-input-field ${loanErrors.loanTerm ? 'calc-input-error' : ''}`}
                                                placeholder="Enter loan term"
                                                value={loanData.loanTerm}
                                                onChange={(e) => setLoanData({ ...loanData, loanTerm: e.target.value })}
                                                min="0"
                                                step="1"
                                            />
                                            {loanErrors.loanTerm && <div className="calc-error-message">{loanErrors.loanTerm}</div>}
                                        </div>
                                    </div>

                                    <div className="calc-input-group">
                                        <label htmlFor="term-type" className="calc-input-label">
                                            <i className="fas fa-clock"></i> Term Type
                                        </label>
                                        <select
                                            id="term-type"
                                            className="calc-select-field"
                                            value={loanData.termType}
                                            onChange={(e) => setLoanData({ ...loanData, termType: e.target.value })}
                                        >
                                            <option value="years">Years</option>
                                            <option value="months">Months</option>
                                        </select>
                                    </div>

                                    <div className="calc-button-group">
                                        <button className="calc-btn calc-btn-primary" onClick={handleLoanCalculate}>
                                            <i className="fas fa-calculator"></i> Calculate Loan
                                        </button>
                                        <button className="calc-btn calc-btn-secondary" onClick={handleReset}>
                                            <i className="fas fa-redo"></i> Reset
                                        </button>
                                    </div>
                                </div>

                                {/* Right Side - Results */}
                                <div className="calc-result-panel">
                                    {loanResult ? (
                                        <div className="calc-result-content">
                                            <div className="calc-result-header">
                                                <h3 className="calc-result-title"><i className="fas fa-chart-bar"></i> Loan Calculation</h3>
                                            </div>

                                            <div className="calc-result-summary">
                                                <div className="calc-summary-card calc-summary-main">
                                                    <div className="calc-summary-label">Monthly Payment</div>
                                                    <div className="calc-summary-value">₹{loanResult.monthlyPayment.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-summary-card">
                                                    <div className="calc-summary-label">Total Interest</div>
                                                    <div className="calc-summary-value">₹{loanResult.totalInterest.toFixed(2)}</div>
                                                </div>
                                            </div>

                                            <div className="calc-result-details">
                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-hand-holding-usd"></i> Loan Amount
                                                    </div>
                                                    <div className="calc-detail-value">₹{loanResult.principal.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-percentage"></i> Annual Rate
                                                    </div>
                                                    <div className="calc-detail-value">{loanResult.annualRate}%</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-calendar-alt"></i> Loan Term
                                                    </div>
                                                    <div className="calc-detail-value">{loanResult.loanTerm} months</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-money-check-alt"></i> Total Interest
                                                    </div>
                                                    <div className="calc-detail-value">₹{loanResult.totalInterest.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-file-invoice-dollar"></i> Total Payment
                                                    </div>
                                                    <div className="calc-detail-value">₹{loanResult.totalPayment.toFixed(2)}</div>
                                                </div>

                                                <div className="calc-detail-row calc-detail-highlight">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-calendar-day"></i> Monthly Payment
                                                    </div>
                                                    <div className="calc-detail-value">₹{loanResult.monthlyPayment.toFixed(2)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="calc-empty-result">
                                            <i className="fas fa-home"></i>
                                            <p>Enter loan details to calculate payments</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* BMI Calculator */}
                    {activeCalculator === 'bmi' && (
                        <div className="calc-wrapper calc-active">
                            <div className="calc-header-wrapper">
                                <h2 className="calc-header-title"><i className="fas fa-weight"></i> BMI Calculator</h2>
                            </div>

                            <div className="calc-side-layout">
                                {/* Left Side - Inputs */}
                                <div className="calc-input-panel">
                                    <div className="calc-input-row">
                                        <div className="calc-input-group">
                                            <label htmlFor="height" className="calc-input-label">
                                                <i className="fas fa-ruler-vertical"></i> Height
                                            </label>
                                            <input
                                                type="number"
                                                id="height"
                                                className={`calc-input-field ${bmiErrors.height ? 'calc-input-error' : ''}`}
                                                placeholder="Enter height"
                                                value={bmiData.height}
                                                onChange={(e) => setBmiData({ ...bmiData, height: e.target.value })}
                                                min="0"
                                                step="0.1"
                                            />
                                            {bmiErrors.height && <div className="calc-error-message">{bmiErrors.height}</div>}
                                        </div>

                                        <div className="calc-input-group">
                                            <label htmlFor="height-unit" className="calc-input-label">
                                                <i className="fas fa-ruler"></i> Height Unit
                                            </label>
                                            <select
                                                id="height-unit"
                                                className="calc-select-field"
                                                value={bmiData.heightUnit}
                                                onChange={(e) => setBmiData({ ...bmiData, heightUnit: e.target.value })}
                                            >
                                                <option value="cm">Centimeters (cm)</option>
                                                <option value="meter">Meters (m)</option>
                                                <option value="feet">Feet (ft)</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="calc-input-row">
                                        <div className="calc-input-group">
                                            <label htmlFor="weight" className="calc-input-label">
                                                <i className="fas fa-weight"></i> Weight
                                            </label>
                                            <input
                                                type="number"
                                                id="weight"
                                                className={`calc-input-field ${bmiErrors.weight ? 'calc-input-error' : ''}`}
                                                placeholder="Enter weight"
                                                value={bmiData.weight}
                                                onChange={(e) => setBmiData({ ...bmiData, weight: e.target.value })}
                                                min="0"
                                                step="0.1"
                                            />
                                            {bmiErrors.weight && <div className="calc-error-message">{bmiErrors.weight}</div>}
                                        </div>

                                        <div className="calc-input-group">
                                            <label htmlFor="weight-unit" className="calc-input-label">
                                                <i className="fas fa-balance-scale"></i> Weight Unit
                                            </label>
                                            <select
                                                id="weight-unit"
                                                className="calc-select-field"
                                                value={bmiData.weightUnit}
                                                onChange={(e) => setBmiData({ ...bmiData, weightUnit: e.target.value })}
                                            >
                                                <option value="kg">Kilograms (kg)</option>
                                                <option value="pounds">Pounds (lb)</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="calc-button-group">
                                        <button className="calc-btn calc-btn-primary" onClick={handleBmiCalculate}>
                                            <i className="fas fa-calculator"></i> Calculate BMI
                                        </button>
                                        <button className="calc-btn calc-btn-secondary" onClick={handleReset}>
                                            <i className="fas fa-redo"></i> Reset
                                        </button>
                                    </div>
                                </div>

                                {/* Right Side - Results */}
                                <div className="calc-result-panel">
                                    {bmiResult ? (
                                        <div className="calc-result-content">
                                            <div className="calc-result-header">
                                                <h3 className="calc-result-title"><i className="fas fa-chart-line"></i> BMI Calculation</h3>
                                            </div>

                                            <div className="calc-result-summary">
                                                <div className="calc-summary-card calc-summary-main" style={{ borderColor: bmiResult.categoryColor }}>
                                                    <div className="calc-summary-label">Your BMI</div>
                                                    <div className="calc-summary-value" style={{ color: bmiResult.categoryColor }}>
                                                        {bmiResult.bmi.toFixed(1)}
                                                    </div>
                                                </div>

                                                <div className="calc-summary-card" style={{ borderColor: bmiResult.categoryColor }}>
                                                    <div className="calc-summary-label">Category</div>
                                                    <div className="calc-summary-value" style={{ color: bmiResult.categoryColor }}>
                                                        {bmiResult.category}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="calc-result-details">
                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-ruler-vertical"></i> Height
                                                    </div>
                                                    <div className="calc-detail-value">{bmiResult.height.toFixed(1)} cm</div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-weight"></i> Weight
                                                    </div>
                                                    <div className="calc-detail-value">{bmiResult.weight.toFixed(1)} kg</div>
                                                </div>

                                                <div className="calc-detail-row calc-detail-highlight" style={{ color: bmiResult.categoryColor }}>
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-heartbeat"></i> BMI Score
                                                    </div>
                                                    <div className="calc-detail-value" style={{ color: bmiResult.categoryColor }}>
                                                        {bmiResult.bmi.toFixed(1)}
                                                    </div>
                                                </div>

                                                <div className="calc-detail-row">
                                                    <div className="calc-detail-label">
                                                        <i className="fas fa-chart-bar"></i> Health Category
                                                    </div>
                                                    <div className="calc-detail-value" style={{ color: bmiResult.categoryColor }}>
                                                        {bmiResult.category}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="calc-bmi-scale">
                                                <div className="calc-scale-item calc-scale-underweight">
                                                    <span>Underweight</span>
                                                    <span>&lt; 18.5</span>
                                                </div>
                                                <div className="calc-scale-item calc-scale-normal">
                                                    <span>Normal</span>
                                                    <span>18.5 - 24.9</span>
                                                </div>
                                                <div className="calc-scale-item calc-scale-overweight">
                                                    <span>Overweight</span>
                                                    <span>25 - 29.9</span>
                                                </div>
                                                <div className="calc-scale-item calc-scale-obese">
                                                    <span>Obese</span>
                                                    <span>≥ 30</span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="calc-empty-result">
                                            <i className="fas fa-weight"></i>
                                            <p>Enter height and weight to calculate BMI</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

           
        </div>
    );
};

export default BgCalculator;